"use client"

import { useEffect, useMemo, useState, type ReactElement } from "react"
import { ThemeSetter } from "../../components/theme/ThemeProvider"

interface Metrics {
  actions_processed: number
  active_validators: number
  daily_contribution: number
  epoch_height: number
  total_supply: number
  circulating: number
  burned: number
  latest_merkle_root: string
  node_status: string
  api_version: string
}

export default function ExplorerPage(): ReactElement {
  const [data, setData] = useState<Metrics | null>(null)

  useEffect(() => {
    async function fetchMetrics() {
      try {
        const res = await fetch("https://phytoncoin.io/api/metrics")
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`)
        }
        const json = await res.json()
        setData(json)
      } catch (err) {
        console.error("Error loading metrics:", err)
      }
    }

    fetchMetrics()
    const interval = setInterval(fetchMetrics, 8000)
    return () => clearInterval(interval)
  }, [])

  const metricCards = useMemo(() => {
    return [
      { label: "Actions Processed", value: data?.actions_processed?.toLocaleString() },
      { label: "Active Validators", value: data?.active_validators?.toLocaleString() },
      { label: "Daily Contribution", value: data?.daily_contribution?.toLocaleString() },
      { label: "Epoch Height", value: data?.epoch_height?.toLocaleString() },
      { label: "Total Supply", value: data?.total_supply?.toLocaleString() },
      { label: "Circulating", value: data?.circulating?.toLocaleString() },
      { label: "Burned", value: data?.burned?.toLocaleString() },
      { label: "Node Status", value: data?.node_status ?? "—" },
    ]
  }, [data])

  return (
    <>
      <ThemeSetter theme="dark" />
      <main className="hero">
        <section className="mx-auto flex max-w-6xl flex-col gap-12">
          <header className="space-y-4 text-center md:text-left">
            <span className="uppercase tracking-[0.4em] text-xs text-primary">Explorer</span>
            <h1 className="font-grotesk text-4xl md:text-5xl">Network Explorer</h1>
            <p className="max-w-3xl text-sm md:text-base" style={{ color: "var(--text)" }}>
              Live network telemetry, refreshed automatically. Epoch height, active validators,
              circulating supply, and ledger state — direct from the node.
            </p>
          </header>

          <section className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {metricCards.map(({ label, value }) => (
              <MetricCard key={label} label={label} value={value} />
            ))}
          </section>

          <section
            className="rounded-2xl border border-muted p-8"
            style={{ background: "var(--surface)", boxShadow: "var(--shadow-soft)" }}
          >
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Network Metadata</h2>
                <ul className="space-y-3 text-sm" style={{ color: "var(--text)" }}>
                  <li className="flex justify-between gap-4">
                    <span style={{ opacity: 0.7 }}>Latest Merkle Root</span>
                    <span className="font-mono text-primary" style={{ wordBreak: "break-word" }}>
                      {data?.latest_merkle_root ?? "—"}
                    </span>
                  </li>
                  <li className="flex justify-between gap-4">
                    <span style={{ opacity: 0.7 }}>API Version</span>
                    <span>{data?.api_version ?? "—"}</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Node Health</h2>
                <p className="text-sm" style={{ color: "var(--text)" }}>
                  Validators synchronize continuously to maintain consensus. The network resolves
                  competing states automatically — no manual intervention required.
                </p>
                <div className="rounded-xl border border-muted p-4" style={{ background: "rgba(30,166,122,0.08)" }}>
                  <p className="text-xs uppercase tracking-[0.24em]" style={{ color: "var(--text)" }}>
                    Current Node Status
                  </p>
                  <p className="mt-2 text-lg font-semibold text-primary">
                    {data?.node_status ?? "Awaiting Sync"}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </section>
      </main>
    </>
  )
}

function MetricCard({
  label,
  value,
}: {
  label: string
  value: string | number | undefined
}) {
  return (
    <article
      className="rounded-2xl border border-muted p-6 text-center transition-transform duration-300 hover:-translate-y-1"
      style={{ background: "var(--surface)", boxShadow: "var(--shadow-soft)", minHeight: "180px" }}
    >
      <p className="text-xs uppercase tracking-[0.24em]" style={{ color: "var(--text)", opacity: 0.8 }}>
        {label}
      </p>
      <p
        className="mt-4 text-3xl font-semibold text-primary"
        style={{ wordBreak: "break-word" }}
      >
        {value ?? "—"}
      </p>
    </article>
  )
}
